import { PermissionsService } from "src/app/core/services/permissions.service";
import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import Swal from "sweetalert2";
import { Subscription } from "rxjs";
import { EventService } from "src/app/core/services/event.service";
import { TranslateService } from "@ngx-translate/core";
import { DevelopmentsService } from "../../../services/developments.services";
import { DOCUMENT_UPLOAD_MAX_BYTES } from "../../../create-edit/documents/document-upload.util";

@Component({
  selector: "app-documents-developments",
  templateUrl: "./documents.component.html",
  styleUrls: ["./documents.component.scss"],
})
export class DocumentsDevelopmentsComponent implements OnInit, OnChanges {
  subscriptionSubmitted!: Subscription;
  countDoc: number = 0;
  documents: any = [];
  @Input()
  propertyDocumentsData!: any;
  canAddDocuments = false;
  selectedDocumentVisibility = "PRIVATE";
  documentsByVisibility: Record<string, any[]> = {
    COMMERCIAL: [],
    INTERNAL: [],
    PRIVATE: [],
  };
  documentSections = [
    {
      visibility: "COMMERCIAL",
      label: "t-m-properties.t-commercial-documents",
      tooltip: "t-m-properties.t-document-visibility-commercial-tooltip",
    },
    {
      visibility: "INTERNAL",
      label: "t-m-properties.t-internal-documents",
      tooltip: "t-m-properties.t-document-visibility-internal-tooltip",
    },
    {
      visibility: "PRIVATE",
      label: "t-m-properties.t-private-documents",
      tooltip: "t-m-properties.t-document-visibility-private-tooltip",
    },
  ];
  moveDocumentSectionsByVisibility: Record<string, any[]> = {
    COMMERCIAL: this.documentSections.filter(
      (section) => section.visibility !== "COMMERCIAL",
    ),
    INTERNAL: this.documentSections.filter(
      (section) => section.visibility !== "INTERNAL",
    ),
    PRIVATE: this.documentSections.filter(
      (section) => section.visibility !== "PRIVATE",
    ),
  };
  loginUser!: {
    id?: string;
    role?: { id?: string; name?: string };
    stores?: any[];
    associatedUsers?:any;
  };
  @Input()
  property: any;
  constructor(
    private eventService: EventService,
    public translate: TranslateService,
    private propertiesService: DevelopmentsService,
    public permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    const { id, role, stores, associatedUsers } = this.propertiesService.getUserAuth();
    this.loginUser = { id, role, stores, associatedUsers };
    this.normalizeDocumentsVisibility();
    this.refreshDocumentPermissions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyDocumentsData'] && this.propertyDocumentsData) {
      this.normalizeDocumentsVisibility();
    }

    if (changes['property']) {
      this.refreshDocumentPermissions();
    }
  }

  private normalizeDocumentsVisibility() {
    if (!this.propertyDocumentsData) return;
    this.propertyDocumentsData.data = (this.propertyDocumentsData?.data ?? []).map((doc: any) =>
      this.normalizeDocumentVisibility(doc),
    );
    this.refreshDocumentsByVisibility();
  }

  private normalizeDocumentVisibility(doc: any) {
    const documentVisibility =
      doc?.documentVisibility ??
      (doc?.description === "finishingMapPropertyDocument" ? "COMMERCIAL" : "PRIVATE");
    return {
      ...doc,
      documentVisibility,
      showOnWebsite:
        doc?.showOnWebsite ?? (doc?.description === "finishingMapPropertyDocument"),
    };
  }

  private refreshDocumentsByVisibility() {
    this.documentsByVisibility = {
      COMMERCIAL: [],
      INTERNAL: [],
      PRIVATE: [],
    };

    (this.propertyDocumentsData?.data ?? []).forEach((doc: any) => {
      const visibility = doc?.documentVisibility ?? "PRIVATE";
      const key = this.documentsByVisibility[visibility] ? visibility : "PRIVATE";
      this.documentsByVisibility[key].push(doc);
    });
  }

  trackByDocumentSection(_: number, section: any) {
    return section?.visibility;
  }

  trackByDocument(_: number, doc: any) {
    return doc?.id ?? doc?.order ?? doc?.name;
  }

  shouldShowDocumentSection(section: any): boolean {
    return (
      this.canAddDocuments ||
      (this.documentsByVisibility[section?.visibility]?.length ?? 0) > 0
    );
  }

  private resolveShowOnWebsite(visibility: string) {
    return visibility === "COMMERCIAL";
  }

  moveDocumentToVisibility(doc: any, visibility: string) {
    doc.documentVisibility = visibility;
    doc.showOnWebsite = this.resolveShowOnWebsite(visibility);
    this.refreshDocumentsByVisibility();
  }

  private hasDocumentWithSameNameInVisibility(fileName: string, visibility: string) {
    return (this.propertyDocumentsData?.data ?? []).some(
      (doc: any) =>
        doc?.name === fileName &&
        (doc?.documentVisibility ?? "PRIVATE") === visibility,
    );
  }

  private createDocumentUploadKey(fileName: string, visibility: string) {
    return `${visibility}_${fileName}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
  }

  private isSameDocument(doc: any, target: any) {
    if (doc?.uploadKey || target?.uploadKey) {
      return doc?.uploadKey === target?.uploadKey;
    }

    return doc?.name === target?.name && doc?.order === target?.order;
  }

  canEditDocument(data: any): boolean {
    return (
      data?.isNew ||
      this.permissionsService.checkMasterPermission(
        "EDIT_PROPERTY_DOCUMENTS",
        this.property?.store?.id,
        data?.isNew,
      ) ||
      this.permissionsService.checkAgentPermission(
        this.property?.mainAgent,
        "EDIT_PROPERTY_DOCUMENTS",
      )
    );
  }

  canUseDocumentActions(data: any): boolean {
    return (
      data?.isNew ||
      !this.property ||
      this.canConsultDocument(data) ||
      this.canDownloadDocument(data) ||
      this.canEditDocument(data) ||
      this.canDeleteDocument(data)
    );
  }

  canConsultDocument(data: any): boolean {
    return (
      !data?.isNew &&
      this.permissionsService.hasPermission("CONSULT_PROPERTY_DOCUMENTS")
    );
  }

  canDownloadDocument(data: any): boolean {
    return (
      !data?.isNew &&
      this.permissionsService.hasPermission("DOWNLOAD_PROPERTY_DOCUMENTS")
    );
  }

  canDeleteDocument(data: any): boolean {
    return (
      data?.isNew ||
      this.permissionsService.checkMasterPermission(
        "DELETE_PROPERTY_DOCUMENTS",
        this.property?.store?.id,
        data?.isNew,
      ) ||
      this.permissionsService.checkAgentPermission(
        this.property?.mainAgent,
        "DELETE_PROPERTY_DOCUMENTS",
      )
    );
  }

  private refreshDocumentPermissions() {
    if (!this.property) {
      this.canAddDocuments = this.permissionsService.hasPermission(
        "ADD_PROPERTY_DOCUMENTS",
      );
      return;
    }

    this.canAddDocuments =
      this.permissionsService.checkMasterPermission(
        "ADD_PROPERTY_DOCUMENTS",
        this.property?.store?.id,
      ) ||
      this.permissionsService.checkAgentPermission(
        this.property?.mainAgent,
        "ADD_PROPERTY_DOCUMENTS",
      );
  }

  getDocumentDescription(doc: any) {
    if (doc?.description === "finishingMapPropertyDocument") {
      return this.translate.instant("t-developments.t-finishing-map");
    }

    return doc?.description;
  }

  someNewDocument(list: any[]) {
    return list && list.some((l) => l?.isNew);
  }

  async onFileChange(event: any) {
    const validFiles = ["jpg", "jpeg", "png", "pdf", "kmz", "doc", "docx"];
    this.countDoc = this.propertyDocumentsData?.data?.length;
    if (event.target?.files && event.target?.files?.length > 0) {
      this.countDoc += event.target.files.length;
      if (this.countDoc <= 50) {
        // let readCount = 0;
        for (var i = 0; i < event.target.files.length; i++) {
          const originalFile: File = event.target.files[i];
          const file = this.normalizeUploadFile(originalFile);
          const fileType = this.resolveFileType(file);
          if (validFiles.includes(fileType)) {
            // setTimeout(() => {
            // Limite por documento; o conjunto de documentos não tem teto.
            if (file.size <= DOCUMENT_UPLOAD_MAX_BYTES) {
              const fileData = this.hasDocumentWithSameNameInVisibility(
                file.name,
                this.selectedDocumentVisibility,
              );
              if (!fileData) {
                let description = "---";
                if (this.selectedDocumentVisibility === "COMMERCIAL") {
                  const commercialDescription =
                    await this.askCommercialDocumentDescription();
                  if (!commercialDescription) continue;
                  description = commercialDescription;
                }
                const uploadKey = this.createDocumentUploadKey(
                  file.name,
                  this.selectedDocumentVisibility,
                );
                (file as any).uploadKey = uploadKey;

                const data = {
                  name: file.name,
                  size: file.size,
                  type: file.type || `application/${fileType}`,
                  createdAt: new Date(),
                  isNew: true,
                  description,
                  documentVisibility: this.selectedDocumentVisibility,
                  showOnWebsite: this.resolveShowOnWebsite(this.selectedDocumentVisibility),
                  uploadKey,
                  order: this.propertyDocumentsData.data.length + 1,
                };

                this.propertyDocumentsData.data.unshift(data);
                this.propertyDocumentsData.dataUpload.unshift(file);
                this.refreshDocumentsByVisibility();
              } else {
                const msg = this.translate.instant(
                  "t-m-properties.t-the-files-must-not-have-the-same-name"
                );
                this.errorMsg(msg);
              }
            } else {
              const msg = this.translate.instant(
                "t-m-properties.t-the-maximum-file-upload-limit-should-not-exceed-50MB"
              );
              this.errorMsg(msg);
            }
          } else {
            const msg = this.translate.instant(
              "t-m-properties.t-document-formats-must-be-images-and-pdf"
            );
            this.errorMsg(msg);
          }

          // }, 10);
        }
        event.target.value = "";
      } else {
        const msg = this.translate.instant(
          "t-m-properties.t-the-number-of-documents-must-be-50"
        );
        this.errorMsg(msg);
      }
    }
  }

  addDoc(visibility = "PRIVATE") {
    this.selectedDocumentVisibility = visibility;
    const inputFile = document.getElementById("input-file-doc");
    inputFile?.click();
  }

  private async askCommercialDocumentDescription(): Promise<string | null> {
    const floorPlan = this.translate.instant(
      "t-m-properties.t-commercial-document-floor-plan",
    );
    const finishingMap = this.translate.instant(
      "t-m-properties.t-commercial-document-finishing-map",
    );
    const other = this.translate.instant(
      "t-m-properties.t-commercial-document-other",
    );

    const result = await Swal.fire({
      title: this.translate.instant("t-m-properties.t-commercial-document-type"),
      html: `
        <div class="text-start">
          <label class="form-label">${this.translate.instant("t-m-properties.t-type")}</label>
          <select id="commercial-document-type" class="form-select mb-3">
            <option value="" selected>${this.translate.instant("t-m-properties.t-commercial-document-type-placeholder")}</option>
            <option value="${floorPlan}">${floorPlan}</option>
            <option value="${finishingMap}">${finishingMap}</option>
            <option value="OTHER">${other}</option>
          </select>
          <div id="commercial-document-other-wrap" style="display:none;">
            <label class="form-label">${this.translate.instant("t-m-properties.t-description")}</label>
            <input id="commercial-document-description" class="form-control"
              maxlength="200"
              placeholder="${this.translate.instant("t-m-properties.t-commercial-document-description-placeholder")}" />
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: this.translate.instant("t-save"),
      cancelButtonText: this.translate.instant("t-close"),
      customClass: { confirmButton: "btn btn-primary-custom" },
      didOpen: () => {
        const select = document.getElementById(
          "commercial-document-type",
        ) as HTMLSelectElement | null;
        const input = document.getElementById(
          "commercial-document-description",
        ) as HTMLInputElement | null;
        const otherWrap = document.getElementById(
          "commercial-document-other-wrap",
        ) as HTMLDivElement | null;

        select?.addEventListener("change", () => {
          if (!input || !select || !otherWrap) return;
          const isOther = select.value === "OTHER";
          otherWrap.style.display = isOther ? "block" : "none";
          input.value = "";
          if (isOther) input.focus();
        });
      },
      preConfirm: () => {
        const select = document.getElementById(
          "commercial-document-type",
        ) as HTMLSelectElement | null;
        const input = document.getElementById(
          "commercial-document-description",
        ) as HTMLInputElement | null;
        const selectedValue = `${select?.value ?? ""}`.trim();
        const value =
          selectedValue === "OTHER"
            ? `${input?.value ?? ""}`.trim()
            : selectedValue;

        if (!value) {
          Swal.showValidationMessage(
            this.translate.instant(
              "t-m-properties.t-commercial-document-description-required",
            ),
          );
          return false;
        }

        return value;
      },
    });

    return result.isConfirmed ? `${result.value}`.trim() : null;
  }

  deleteDoc(doc: any) {
    Swal.fire({
      title: this.translate.instant(
        "t-m-properties.t-are-you-sure-that-you-want-to-delete-a-document"
      ),
      //text: 'Deleting your property will remove all of your information from our database.',
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f46a6a",
      confirmButtonText: this.translate.instant("t-yes"),
      cancelButtonText: this.translate.instant("t-close"),
    }).then((result) => {
      if (result.value) {
        if (doc.isNew) {
          this.propertyDocumentsData.data =
            this.propertyDocumentsData.data.filter(
              (d: any) => !this.isSameDocument(d, doc)
            );
          this.propertyDocumentsData.dataUpload =
            this.propertyDocumentsData.dataUpload.filter(
              (d: any) =>
                doc.uploadKey
                  ? (d as any).uploadKey !== doc.uploadKey
                  : d.name !== doc.name
            );
        } else {
          this.propertyDocumentsData.data =
            this.propertyDocumentsData.data.filter(
              (d: any) => d.id !== doc.id && d.order !== doc.order
            );
          this.propertyDocumentsData.dataDeletefiles.push(doc);
        }
        let orderMax = this.propertyDocumentsData.data.length;
        this.propertyDocumentsData.data.forEach((el: any) => {
          el.order = orderMax;
          orderMax--;
        });
        this.refreshDocumentsByVisibility();
      }
    });
  }

  editDocument(doc: any) {
    const msg = this.translate.instant(
      "t-m-properties.t-description-must-not-exceed-200-characters"
    );
    Swal.fire({
      title: `${this.translate.instant(
        "t-m-properties.t-edit-document-description"
      )}: ${doc.name}`,
      input: "textarea",
      inputAttributes: {
        autocapitalize: "off",
      },
      showCancelButton: true,
      confirmButtonText: this.translate.instant("t-save"),
      cancelButtonText: this.translate.instant("t-close"),
      showLoaderOnConfirm: true,
      inputValue: doc.description,
      customClass: { confirmButton: "btn btn-primary-custom" },
      preConfirm(inputValue) {
        if (inputValue.length > 200) {
          return Swal.showValidationMessage(msg);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed) {
        const inputValue = result.value as unknown as string;
        if (inputValue) doc.description = inputValue;
        else doc.description = "---";
      }
    });
  }

  createDateFormat(date: any) {
    const d = new Date(date);
    return d.toLocaleString();
  }

  formatType(type: any) {
    if (!type || typeof type !== "string") return "";
    const split = type.split("/");
    return (split[1] || "").toLowerCase();
  }

  /** Word (.doc/.docx), pelo mimetype ou, à falta dele, pela extensão do nome. */
  isWord(doc: any): boolean {
    if (this.wordTypeOf(doc?.type)) return true;
    const extension = `${doc?.name ?? ""}`.split(".").pop()?.toLowerCase();
    return extension === "doc" || extension === "docx";
  }

  documentIconName(doc: any): string {
    if (this.formatType(doc?.type) === "pdf") return "ri-file-pdf-fill";
    if (this.isWord(doc)) return "ri-file-word-fill";
    return "ri-image-2-line";
  }

  documentIconColor(doc: any): string {
    if (this.formatType(doc?.type) === "pdf") return "text-danger";
    if (this.isWord(doc)) return "text-info";
    return "text-primary";
  }

  private wordTypeOf(type: any): "doc" | "docx" | null {
    const mime = `${type ?? ""}`.toLowerCase();
    if (mime.includes("wordprocessingml")) return "docx";
    if (mime.endsWith("/msword")) return "doc";
    return null;
  }

  resolveFileType(file: any) {
    const extension = file?.name?.split(".")?.pop()?.toLowerCase();
    if (extension === "kmz") return "kmz";
    if (extension === "doc" || extension === "docx") return extension;
    const wordType = this.wordTypeOf(file?.type);
    if (wordType) return wordType;

    const mimeType = this.formatType(file?.type);
    if (mimeType) return mimeType;
    return extension || "";
  }

  normalizeUploadFile(file: File) {
    const extension = file?.name?.split(".")?.pop()?.toLowerCase();
    if (extension === "kmz" && file.type !== "application/vnd.google-earth.kmz") {
      return new File([file], file.name, {
        type: "application/vnd.google-earth.kmz",
        lastModified: file.lastModified,
      });
    }
    return file;
  }

  getDocumentPreviewUrl(path: string) {
    if (!path) return path;
    if (path.endsWith("/preview")) return path;
    return `${path}/preview`;
  }

  previewDocument(doc: any) {
    const docPath = doc?.path;
    if (!docPath) return;

    const url = this.getDocumentPreviewUrl(docPath);
    // O browser não mostra Word em iframe: abre/descarrega noutro separador.
    if (this.isWord(doc)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const fileType = this.formatType(doc.type);
    const isPdf = fileType === "pdf";
    const isImage = ["png", "jpg", "jpeg", "webp"].includes(fileType);

    const previewHtml = isPdf
      ? `<iframe src="${url}" style="width:100%;height:75vh;border:0;" title="document-preview"></iframe>`
      : isImage
        ? `<div class="text-center"><img src="${url}" alt="${doc.name}" style="max-width:100%;max-height:75vh;object-fit:contain;" /></div>`
        : `<iframe src="${url}" style="width:100%;height:75vh;border:0;" title="document-preview"></iframe>`;

    Swal.fire({
      title: `${this.translate.instant("t-preview")}: ${doc.name}`,
      html: previewHtml,
      width: "80rem",
      showCloseButton: true,
      showConfirmButton: false,
    });
  }

  openDocumentQuickly(doc: any) {
    const docPath = doc?.path;
    if (!docPath) return;

    const url = this.getDocumentPreviewUrl(docPath);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  formatSizeUnits(bytes: any) {
    if (bytes >= 1073741824) {
      bytes = (bytes / 1073741824).toFixed(2) + " GB";
    } else if (bytes >= 1048576) {
      bytes = (bytes / 1048576).toFixed(2) + " MB";
    } else if (bytes >= 1024) {
      bytes = (bytes / 1024).toFixed(2) + " KB";
    } else if (bytes > 1) {
      bytes = bytes + " bytes";
    } else if (bytes == 1) {
      bytes = bytes + " byte";
    } else {
      bytes = "0 bytes";
    }
    return bytes;
  }

  checkSize(bytes: any) {
    bytes = (bytes / 1048576).toFixed(2);
    return bytes;
  }
  getTotalSize() {
    let filesSize = 0;
    this.propertyDocumentsData.data.forEach((el: any) => {
      filesSize += el.size;
    });
    return this.formatSizeUnits(filesSize);
  }

  /**
   * Title sweet alert
   * @param Error Msg modal content
   */
  errorMsg(errorMsg: string) {
    Swal.fire({
      title: "Oops...",
      text: errorMsg,
      icon: "warning",
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: this.translate.instant("t-close"),
    });
  }

  projectListWidgets = [
    {
      icon: "ri-folder-zip-line",
      icon_color: "secondary",
      name: "Artboard-documents.zip",
      type: "Zip",
      size: "4.57",
      date: "12 Dec 2021",
    },
    {
      icon: "ri-file-pdf-fill",
      icon_color: "danger",
      name: "Bank Management System",
      type: "PDF",
      size: "8.89",
      date: "24 Nov 2021",
    },
    {
      icon: "ri-video-line",
      icon_color: "secondary",
      name: "Tour-video.mp4",
      type: "MP4",
      size: "14.62",
      date: "19 Nov 2021",
    },
    {
      icon: "ri-file-excel-fill",
      icon_color: "success",
      name: "Account-statement.xsl",
      type: "XSL",
      size: "2.38",
      date: "14 Nov 2021",
    },
    {
      icon: "ri-folder-fill",
      icon_color: "warning",
      name: "Project Screenshots Collection",
      type: "Floder",
      size: "87.24",
      date: "08 Nov 2021",
    },
    {
      icon: "ri-image-2-fill",
      icon_color: "danger",
      name: "Velzon-logo.png",
      type: "PNG",
      size: "879",
      date: "02 Nov 2021",
    },
  ];
}
